/**
 * RTK Query Base Query Configuration
 * Bridges our existing apiClient with RTK Query
 * This allows RTK Query to leverage all our existing interceptors and token management
 */

import { axiosInstance as apiClient } from '../../services/apiClient';

/**
 * Custom base query that uses our existing apiClient
 * This ensures all RTK Query requests go through our interceptors
 */
const baseQuery = async (args, api, extraOptions) => {
  // Support both string URLs and object configurations
  const { url, method = 'GET', data, body, params, headers, ...rest } =
    typeof args === 'string' ? { url: args } : args;

  try {
    // Make the request using our apiClient
    // RTK Query endpoints use `body`, axios uses `data` — accept both
    const dataToSend = data || body;
    const isFormData = typeof FormData !== 'undefined' && dataToSend instanceof FormData;
    const result = await apiClient({
      url,
      method,
      data: dataToSend,
      params,
      // When body is FormData, clear Content-Type so axios auto-sets
      // multipart/form-data with the correct boundary (overrides the
      // instance default of application/json)
      headers: isFormData
        ? { ...headers, 'Content-Type': undefined }
        : headers,
      ...rest,
    });

    // Return in RTK Query format
    return {
      data: result.data,
      meta: {
        request: result.request,
        response: result,
      },
    };
  } catch (axiosError) {
    // Handle different error scenarios
    let error = {
      status: axiosError.response?.status || 'CUSTOM_ERROR',
      data: axiosError.response?.data || axiosError.message,
    };

    // Special handling for 401 errors
    if (axiosError.response?.status === 401) {
      // The apiClient interceptor already handles token refresh
      // If we get here, the refresh failed
      error.data = {
        message: 'Session expired. Please login again.',
        ...error.data,
      };
    }

    // Special handling for network errors
    if (!axiosError.response) {
      error = {
        status: 'FETCH_ERROR',
        data: 'Network error. Please check your connection.',
      };
    }

    return { error };
  }
};

/**
 * Enhanced base query with automatic retry logic
 */
const baseQueryWithRetry = async (args, api, extraOptions) => {
  const maxRetries = extraOptions?.maxRetries ?? 0;
  let retries = 0;

  while (retries <= maxRetries) {
    const result = await baseQuery(args, api, extraOptions);
    
    // If successful or not a retryable error, return immediately
    if (!result.error || result.error.status !== 'FETCH_ERROR') {
      return result;
    }

    retries++;
    
    if (retries <= maxRetries) {
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries - 1)));
    }
  }

  return await baseQuery(args, api, extraOptions);
};

/**
 * Base query with authentication state management
 * Dispatches Redux actions when auth state changes
 */
const baseQueryWithAuth = async (args, api, extraOptions) => {
  const result = await baseQueryWithRetry(args, api, extraOptions);

  // 401 handling is fully managed by the apiClient axios interceptor,
  // which coalesces refresh attempts. Dispatching clearAuth here would
  // race with it and cause premature logouts when many queries fire at once.

  return result;
};

// Export the enhanced base query
export default baseQueryWithAuth;

// Also export the simple base query for cases where we don't need auth handling
export { baseQuery, baseQueryWithRetry };